import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, FileCheck2, CalendarDays, CheckSquare, Clock3, FileSignature, Wallet, Trash2, Plus, ShieldCheck, FileText,
} from "lucide-react";
import { Btn, Badge, Spinner, Avatar } from "@/components/ui-kit";
import api, { API, docFileUrl } from "@/lib/api";
import Modal from "@/pages/ops/Modal";
import RosterViews from "@/components/RosterViews";
import ShiftEditModal from "@/components/ShiftEditModal";
import ShiftCheck, { useShiftCheck } from "@/components/ShiftCheck";
import { ScoreBadge, StarRating, METRIC_LABELS, METRIC_HELP } from "@/components/PerfScore";
import ClinicianStatusControls, { ClinicianStatusBadges, ShiftWarning, StatusLog, isAvailable } from "@/pages/ops/ClinicianStatus";
import LegalAcceptances from "@/pages/ops/LegalAcceptances";
import CertifiedBadge from "@/components/CertifiedBadge";

const money = (n, c = "₹") => `${c}${Number(n || 0).toLocaleString("en-IN")}`;
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const REQUIRED = [
  { key: "photo", label: "Profile photo" },
  { key: "registration", label: "Council registration" },
  { key: "membership", label: "Membership proof" },
  { key: "insurance", label: "Indemnity insurance" },
  { key: "insurance_waiver", label: "Indemnity insurance waiver (in lieu of policy)" },
];

export default function ClinicianDetail({ cid, onClose, onChange, initialTab = "performance" }) {
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/ops/clinicians/${cid}`).then((r) => setData(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const TABS = [
    { k: "performance", label: "Performance", icon: Activity },
    { k: "documents", label: "Documents", icon: FileCheck2 },
    { k: "roster", label: "Roster", icon: CalendarDays },
    { k: "attendance", label: "Attendance", icon: CheckSquare },
    { k: "availability", label: "Availability", icon: Clock3 },
    { k: "agreements", label: "Agreements", icon: FileSignature },
    { k: "legal", label: "T&C acceptance", icon: ShieldCheck },
    { k: "invoices", label: "Invoices", icon: Wallet },
  ];

  const c = data?.clinician;
  return (
    <Modal wide onClose={onClose}
      title={c ? c.name : "Clinician"}
      subtitle={c ? `${c.specialty || "—"} · ${c.city || "—"} · ${c.prq_id || "—"}` : ""}>
      {!data ? <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div> : (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Avatar name={c.name} size={40} src={docFileUrl(c.photo_doc_id)} />
            <CertifiedBadge comp={c.compliance} size={20} showMissing testid="ops-detail-certified" />
            <Badge tone={c.credential_status === "Verified" ? "emerald" : "amber"}>{c.credential_status || "Pending"}</Badge>
            <ClinicianStatusBadges c={c} />
            <div className="sm:ml-auto"><ClinicianStatusControls c={c} onChange={() => { load(); onChange && onChange(); }} /></div>
            {c.credential_status !== "Verified" && (
              <Btn size="sm" variant="primary" data-testid="ops-verify-clinician" disabled={(c.missing_docs || []).length > 0}
                onClick={async () => {
                  try { await api.put(`/ops/clinicians/${cid}/verify`, { credential_status: "Verified" }); toast.success("Clinician verified"); load(); onChange && onChange(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Could not verify"); }
                }}>
                <ShieldCheck size={14} /> Verify clinician
              </Btn>
            )}
            {c.credential_status !== "Verified" && (c.missing_docs || []).length > 0 && (
              <span className="text-[11.5px] text-amber-600" data-testid="ops-verify-blocked">Missing required: {c.missing_docs.map((m) => m === "insurance" ? "insurance or waiver" : m).join(", ")}</span>
            )}
          </div>
          <div className="mb-4"><ShiftWarning c={c} onChange={() => { load(); onChange && onChange(); }} /></div>
          <StatusLog log={c.status_log} />
          <div className="flex gap-1.5 flex-wrap border-b border-slate-100 pb-3 mb-4">
            {TABS.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ops-detail-tab-${t.k}`}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${tab === t.k ? "bg-navy-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>

          {tab === "performance" && <Performance perf={data.perf} />}
          {tab === "documents" && <Documents cid={cid} docs={data.clinician.documents} onChange={load} />}
          {tab === "roster" && <Roster cid={cid} roster={data.roster} patients={data.assigned_patients || []} onChange={load} available={isAvailable(c)} />}
          {tab === "attendance" && <Attendance cid={cid} attendance={data.attendance} onChange={load} />}
          {tab === "availability" && <Availability cid={cid} availability={data.availability} onChange={load} />}
          {tab === "agreements" && <Agreements cid={cid} agreements={data.agreements} onChange={() => { load(); onChange && onChange(); }} />}
          {tab === "invoices" && <Invoices invoices={data.invoices} />}
          {tab === "legal" && <LegalAcceptances cid={cid} />}
        </>
      )}
    </Modal>
  );
}

function Performance({ perf }) {
  const m = perf.metrics || {};
  const cards = [
    { label: "Active cases", value: perf.active_cases },
    { label: "Completed", value: perf.completed_cases },
    { label: "Sessions done", value: `${perf.sessions_done}/${perf.sessions_total}` },
    { label: "Payout earned", value: money(perf.payout_earned) },
    { label: "Payout pending", value: money(perf.payout_pending) },
  ];
  return (
    <div data-testid="ops-performance">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl bg-navy-50 p-4" data-testid="ops-perf-score">
          <div className="text-xl"><ScoreBadge score={perf.perf_score} isNew={perf.is_new} /></div>
          <div className="text-[11.5px] text-slate-500 mt-1">Performance score · weighted from {perf.metrics_used?.length || 0} measured metric{perf.metrics_used?.length === 1 ? "" : "s"}{perf.is_new ? " · limited data (under 3 sessions)" : ""}</div>
        </div>
        <div className="rounded-xl bg-amber-50/60 p-4" data-testid="ops-perf-stars">
          <div className="text-xl"><StarRating value={perf.patient_rating} count={perf.patient_rating_count} /></div>
          <div className="text-[11.5px] text-slate-500 mt-1">Patient rating · 1–5 stars after each session</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        {Object.keys(METRIC_LABELS).map((k) => (
          <div key={k} className="rounded-xl bg-slate-50 p-4" title={METRIC_HELP[k]} data-testid={`ops-metric-${k}`}>
            <div className="metric text-xl font-extrabold text-slate-900">{m[k] == null ? "—" : `${m[k]}%`}</div>
            <div className="text-[11.5px] text-slate-500 mt-1">{METRIC_LABELS[k]}</div>
            <div className="text-[10.5px] text-slate-400 mt-0.5">{m[k] == null ? "No data yet" : METRIC_HELP[k]}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl bg-slate-50 p-4">
            <div className="metric text-lg font-extrabold text-slate-900">{c.value}</div>
            <div className="text-[11.5px] text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Documents({ cid, docs, onChange }) {
  const present = {};
  docs.forEach((d) => { if (!present[d.doc_type]) present[d.doc_type] = d; });
  const token = localStorage.getItem("praniq_token");
  const fileUrl = (d) => `${API}/documents/${d.id}/file?auth=${token}`;
  const setStatus = async (docId, status) => {
    await api.put(`/ops/documents/${docId}`, { verification_status: status });
    toast.success(`Document ${status}`); onChange();
  };
  return (
    <div className="space-y-2.5" data-testid="ops-documents">
      {REQUIRED.map((r) => {
        const d = present[r.key];
        return (
          <div key={r.key} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
            {d ? (
              <a href={fileUrl(d)} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`ops-doc-view-${r.key}`} title="View document">
                <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200 hover:border-teal-500 transition-colors">
                  {(d.content_type || "").startsWith("image/")
                    ? <img src={fileUrl(d)} alt={r.label} className="w-full h-full object-cover" />
                    : <FileText size={20} className="text-slate-400" />}
                </div>
              </a>
            ) : <FileCheck2 size={18} className="text-slate-300 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold">{r.label}</div>
              {d ? (
                <a href={fileUrl(d)} target="_blank" rel="noreferrer" className="text-[11.5px] text-teal-600 hover:underline truncate block" data-testid={`ops-doc-open-${r.key}`}>
                  {d.original_filename || "Uploaded"} — view
                </a>
              ) : <div className="text-[11.5px] text-slate-500">Not uploaded by clinician</div>}
            </div>
            {d ? (
              <>
                <Badge tone={d.verification_status === "Verified" ? "emerald" : d.verification_status === "Rejected" ? "rose" : "amber"}>{d.verification_status}</Badge>
                {d.verification_status !== "Verified" && <Btn size="sm" onClick={() => setStatus(d.id, "Verified")} data-testid={`ops-doc-verify-${r.key}`}>Verify</Btn>}
                {d.verification_status !== "Rejected" && <Btn size="sm" className="pq-btn-danger" onClick={() => setStatus(d.id, "Rejected")} data-testid={`ops-doc-reject-${r.key}`}>Reject</Btn>}
              </>
            ) : <Badge tone="slate">Missing</Badge>}
          </div>
        );
      })}
    </div>
  );
}

function Roster({ cid, roster, patients = [], onChange, available = true }) {
  const [f, setF] = useState({ date: "", start: "09:00", end: "17:00", location: "Home visits", note: "", patient_id: "" });
  const add = async () => {
    if (!f.date) return toast.error("Pick a date");
    try {
      await api.post(`/ops/clinicians/${cid}/roster`, f);
      toast.success("Shift added"); setF({ ...f, date: "", note: "", patient_id: "" }); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add shift"); }
  };
  const [edit, setEdit] = useState(null);
  const check = useShiftCheck({ clinicianId: cid, date: f.date, start: f.start, end: f.end, patientId: f.patient_id });
  const del = async (s) => { if (!window.confirm(`Remove the ${s.date} ${s.start} shift?`)) return; await api.delete(`/ops/roster/${s.id}`); toast.success("Shift removed"); onChange(); };
  return (
    <div data-testid="ops-roster">
      {!available ? (
        <p className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-3" data-testid="ops-roster-unavailable">
          This clinician is inactive or suspended — new shifts can't be added. Existing shifts are kept below; edit or reassign them as needed.
        </p>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
        <input type="date" className="pq-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="ops-roster-date" />
        <input type="time" className="pq-input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <input type="time" className="pq-input" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
        <input className="pq-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location" />
        <select className="pq-input" value={f.patient_id} onChange={(e) => setF({ ...f, patient_id: e.target.value })} data-testid="ops-roster-patient">
          <option value="">No patient (general shift)</option>
          {patients.map((p) => <option key={p.id || p.name} value={p.id}>{p.name}</option>)}
        </select>
        <Btn variant="primary" onClick={add} data-testid="ops-roster-add"><Plus size={14} /> Add</Btn>
        <div className="col-span-2 sm:col-span-6 -mt-1"><ShiftCheck data={check} prefix="ops-roster-check" onPickDate={(d) => setF({ ...f, date: d.date, start: d.start, end: d.end })} /></div>
      </div>
      )}
      {patients.length === 0 && <p className="text-[11.5px] text-amber-600 mb-3" data-testid="ops-roster-no-patients">No patients in the system yet.</p>}
      <RosterViews shifts={roster} onEdit={setEdit} onDelete={del} prefix="ops-roster" />
      {edit && <ShiftEditModal shift={edit} patients={patients} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChange(); }} />}
    </div>
  );
}

function Attendance({ cid, attendance, onChange }) {
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), status: "Present", late: false });
  const mark = async () => {
    await api.post(`/ops/clinicians/${cid}/attendance`, f);
    toast.success("Attendance saved"); onChange();
  };
  return (
    <div data-testid="ops-attendance">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 items-end">
        <input type="date" className="pq-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="ops-att-date" />
        <select className="pq-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="ops-att-status">
          {["Present", "Absent", "Leave", "Half-day"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-[13px] text-slate-600"><input type="checkbox" checked={f.late} onChange={(e) => setF({ ...f, late: e.target.checked })} data-testid="ops-att-late" /> Marked late</label>
        <Btn variant="primary" onClick={mark} data-testid="ops-att-mark">Save</Btn>
      </div>
      {attendance.length === 0 ? <p className="text-[12.5px] text-slate-400 py-6 text-center">No attendance recorded.</p> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Date", "Status", "Late"].map((h) => <th key={h} className="pq-label px-3 py-2 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {attendance.map((a) => (
              <tr key={a.date} className="border-b border-slate-100 last:border-0" data-testid={`ops-att-row-${a.date}`}>
                <td className="px-3 py-2 text-[13px] font-semibold">{a.date}</td>
                <td className="px-3 py-2"><Badge tone={a.status === "Present" ? "emerald" : a.status === "Absent" ? "rose" : "amber"}>{a.status}</Badge></td>
                <td className="px-3 py-2 text-[13px]">{a.late ? "Yes" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function Availability({ cid, availability, onChange }) {
  const [slots, setSlots] = useState(availability?.slots || {});
  const toggle = (day) => setSlots((s) => ({ ...s, [day]: !s[day] }));
  const save = async () => { await api.put(`/ops/clinicians/${cid}/availability`, { slots }); toast.success("Availability saved"); onChange(); };
  return (
    <div data-testid="ops-availability">
      <p className="text-[12.5px] text-slate-500 mb-3">Mark the days this clinician is available for assignment.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {WEEK.map((d) => (
          <button key={d} onClick={() => toggle(d)} data-testid={`ops-avail-${d}`}
            className={`w-14 h-14 rounded-xl text-[13px] font-bold transition-colors ${slots[d] ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{d}</button>
        ))}
      </div>
      <Btn variant="primary" onClick={save} data-testid="ops-avail-save">Save availability</Btn>
    </div>
  );
}

function Agreements({ cid, agreements, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const setStatus = async (aid, status) => { await api.put(`/ops/agreements/${aid}`, { status }); toast.success(`Agreement ${status}`); onChange(); };
  return (
    <div data-testid="ops-agreements-tab">
      <div className="flex justify-end mb-3"><Btn variant="primary" onClick={() => setShowAdd(true)} data-testid="ops-agreement-add"><Plus size={14} /> New agreement</Btn></div>
      {agreements.length === 0 ? <p className="text-[12.5px] text-slate-400 py-6 text-center">No agreements on file.</p> : (
        <div className="space-y-2.5">
          {agreements.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 p-3.5" data-testid={`ops-agreement-item-${a.id}`}>
              <div className="flex items-center gap-2">
                <FileSignature size={17} className="text-teal-600" />
                <div className="flex-1"><div className="text-[13.5px] font-semibold">{a.title}</div><div className="text-[11.5px] text-slate-500">{a.type} · {a.commission_pct}% commission · {a.start_date || "—"} → {a.end_date || "ongoing"}</div></div>
                <Badge tone={a.status === "Active" ? "emerald" : a.status === "Expired" ? "rose" : "amber"}>{a.status}</Badge>
              </div>
              <div className="flex gap-2 mt-2.5">
                {a.status !== "Active" && <Btn size="sm" variant="primary" onClick={() => setStatus(a.id, "Active")} data-testid={`ops-agreement-activate-${a.id}`}>Activate</Btn>}
                {a.status === "Active" && <Btn size="sm" onClick={() => setStatus(a.id, "Expired")} data-testid={`ops-agreement-expire-${a.id}`}>Mark expired</Btn>}
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && <AddAgreement cid={cid} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); onChange(); }} />}
    </div>
  );
}

function AddAgreement({ cid, onClose, onDone }) {
  const [f, setF] = useState({ title: "Vendor Service Agreement", type: "Vendor Service Agreement", commission_pct: 20, start_date: "", end_date: "", status: "Draft", note: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await api.post(`/ops/clinicians/${cid}/agreements`, f); toast.success("Agreement created"); onDone(); } catch { toast.error("Failed"); } setSaving(false); };
  return (
    <Modal title="New Agreement" onClose={onClose}>
      <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Title</span><input className="pq-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ops-agr-title" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Commission %</span><input type="number" className="pq-input" value={f.commission_pct} onChange={(e) => setF({ ...f, commission_pct: Number(e.target.value) })} data-testid="ops-agr-commission" /></label>
        <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Status</span>
          <select className="pq-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="ops-agr-status">{["Draft", "Active", "Expired"].map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Start date</span><input type="date" className="pq-input" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></label>
        <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">End date</span><input type="date" className="pq-input" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></label>
      </div>
      <div className="flex justify-end gap-2 mt-2"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={save} disabled={saving} data-testid="ops-agr-submit">{saving ? <Spinner className="w-4 h-4" /> : "Create"}</Btn></div>
    </Modal>
  );
}

function Invoices({ invoices }) {
  return (
    <div data-testid="ops-detail-invoices">
      {invoices.length === 0 ? <p className="text-[12.5px] text-slate-400 py-6 text-center">No vendor invoices for this clinician yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Invoice", "Period", "Amount", "Status"].map((h) => <th key={h} className="pq-label px-3 py-2 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2 text-[13px] font-semibold">{i.invoice_no}</td>
                <td className="px-3 py-2 text-[13px]">{i.period}</td>
                <td className="px-3 py-2 metric text-[13px] font-bold">{money(i.amount, i.currency)}</td>
                <td className="px-3 py-2"><Badge tone={i.status === "Paid" ? "emerald" : i.status === "Approved" ? "sky" : "amber"}>{i.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
